/**
 * Month arithmetic for the Availability report.
 *
 * Months are `"YYYY-MM-01"` strings and days are `"YYYY-MM-DD"` strings. All
 * arithmetic is on integer month ordinals (`year * 12 + monthIndex`), never a
 * parsed local Date, which would drift a month west of Greenwich. "Today" is
 * always passed in by the caller so every rule here is deterministic.
 *
 * 🔒 FIREWALL: the Availability report is HOURS (Utilization). Nothing in
 * `src/lib/availability/` imports Fit Score, Capacity, Intensity, or points modules.
 */

const MONTH_START = /^(\d{4})-(0[1-9]|1[0-2])-01$/;
const DAY = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

const SHORT_MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

export interface DayParts {
  year: number;
  /** 1–12. */
  month: number;
  /** 1–31. */
  day: number;
}

/** True for a `"YYYY-MM-01"` month start. */
export function isMonthStart(value: string): boolean {
  return MONTH_START.test(value);
}

/** Split a `"YYYY-MM-DD"` day into numbers. Throws on a malformed day. */
export function parseDay(value: string): DayParts {
  const match = DAY.exec(value);
  const [, y, m, d] = match ?? [];
  if (y === undefined || m === undefined || d === undefined) {
    throw new RangeError(`Expected a "YYYY-MM-DD" day, got "${value}"`);
  }
  return { year: Number(y), month: Number(m), day: Number(d) };
}

/** The month a day falls in: `"2026-10-16"` → `"2026-10-01"`. */
export function monthStartOf(day: string): string {
  const { year, month } = parseDay(day);
  return fromOrdinal(year * 12 + (month - 1));
}

/** Integer month ordinal of a month start. Throws on a malformed month. */
function toOrdinal(month: string): number {
  const match = MONTH_START.exec(month);
  const [, y, m] = match ?? [];
  if (y === undefined || m === undefined) {
    throw new RangeError(`Expected a "YYYY-MM-01" month, got "${month}"`);
  }
  return Number(y) * 12 + (Number(m) - 1);
}

function fromOrdinal(ordinal: number): string {
  const year = Math.floor(ordinal / 12);
  const month = ordinal - year * 12 + 1;
  return `${year}-${String(month).padStart(2, "0")}-01`;
}

/** Shift a month start by a whole number of months (negative shifts back). */
export function addMonths(month: string, count: number): string {
  return fromOrdinal(toOrdinal(month) + count);
}

/** Months from `from` to `to`. Zero when equal, negative when `to` is earlier. */
export function monthDiff(from: string, to: string): number {
  return toOrdinal(to) - toOrdinal(from);
}

/** `count` consecutive month starts beginning at `from`. Empty when `count <= 0`. */
export function monthRange(from: string, count: number): string[] {
  const start = toOrdinal(from);
  return Array.from({ length: Math.max(0, count) }, (_, i) => fromOrdinal(start + i));
}

/** `"2026-10-01"` → `"Oct 2026"`, or `"Oct"` with `{ short: true }`. */
export function monthLabel(month: string, options: { short?: boolean } = {}): string {
  const ordinal = toOrdinal(month);
  const year = Math.floor(ordinal / 12);
  const name = SHORT_MONTH_NAMES[ordinal - year * 12] ?? month;
  return options.short === true ? name : `${name} ${year}`;
}

/** Whole days from `from` to `to` (both `"YYYY-MM-DD"`). Negative when `to` is earlier. */
export function dayDiff(from: string, to: string): number {
  const a = parseDay(from);
  const b = parseDay(to);
  const msPerDay = 86_400_000;
  return Math.round(
    (Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) / msPerDay,
  );
}
